import type { ConstraintScope } from '@chess/api-constraints';
// The `review` interface: the game open on the review screen, the ply the
// reader is on, and the ways to move through it. The review screen provides
// it; the scoresheet, the board, the step controls and every tab read it.
// Nothing here knows how a game is stored or drawn.
//
// The reader may also leave the game: a move played on the board or picked
// from the shortlist that is not the game's next move starts a line, as
// v1's exploration did. While a line is open the position is the line's:
// stepping forward is suspended, stepping back takes back the line's last
// move one at a time (the last take-back lands on the ply it branched from),
// so the reader can try another move at any point of the line; `toStart`
// and `returnToGame` drop the line.
//
// While a puzzle owns the board, the reader may still look back: `browse`
// shows an earlier position along the path the board has travelled, the
// game up to its ply and then the line, without moving that path's head.
// Nothing can be played while looking back, and leaving the view returns
// to the head exactly as it was.
import type { MoveInput } from '@chess/api-chess';
import { type ReviewPhases } from '@chess/api-model';
import type { ReviewSides } from './sides.ts';
import { type Fen, type GameResult, type Move, type San, type Color } from '@chess/api-model';
import type { Stages } from './stages.ts';
import type { GameScan } from './game-scan.ts';
import type { GameBook } from './game-book.ts';
import type { ReviewServices, ReviewTheory } from './session.ts';
import { ReviewSession } from './session.ts';

export { Observable, type Listener } from './observable.ts';
export { Score, MATE_PAWNS, PAWNS_PER_BAR, sideToMove, type EngineSideScore, type ReaderScore } from './score.ts';
export { Stages } from './stages.ts';
export { Line, type LineEntry } from './line.ts';
export { MOVE_GRADES, MOVE_GRADE_LABEL, moveGradeOf, type MoveGrade } from './move-grade.ts';
export { GameScan, isBlunderLoss, isBlunderUnder, isMissOf, moveKindOf, MISS_GAP_CP, MISS_HOLD_CP } from './game-scan.ts';
export { GameBook, lineMoveFrom, movePrefixOf, variationLine } from './game-book.ts';
export { ReviewSession, referenceSide, type ReviewServices, type ReviewTheory } from './session.ts';
export { ReviewWorkspace, createWorkspace, MAX_EXTRA_TABS, type ReviewTab, type ReviewTabs } from './workspace.ts';

export type { BoardArrow, BoardBrush, BoardInput, BoardLock, BoardOutcome, BoardView, PendingPromotion, ShortlistReading } from './board.ts';

export { readerSideOf, sidesOf, referenceOf, type ReviewSides } from './sides.ts';
export { clocksOf, initialClockOf } from './clocks.ts';

export { endgameStartOf, isEndgame, openingEndOf, phaseOfMove, phaseRange, FALLBACK_OPENING_PLIES, ENDGAME_MATERIAL, ENDGAME_PIECES, moverOf, scoreForReader, readerPawns, readerScoreText, readerMoveAfter, openingExit } from './phases.ts';
export type { ScoredPosition } from './phases.ts';

export const REVIEW_API = 'review';
export const REVIEW_VERSION = '2.0.0';

/**
 * The `phases` interface: who decides where a game's opening ends and its
 * endgame begins. The review's loader asks it when a game opens and stores
 * the answer with the game; a build without a provider falls back to the
 * rules in `phases.ts` with no opening book.
 */
export const PHASES_API = 'phases';
export interface PhasesProvider {
  phasesOf(game: { moves: Move[]; positions: Fen[] }): Promise<ReviewPhases>;
}

/** A game as the review needs it: the moves and the position after each. */
export interface ReviewGame {
  id: string;
  moves: Move[];
  /** `positions[0]` is the start; `positions[i]` is after `moves[i - 1]`. */
  positions: Fen[];
  result: GameResult;
  /** Where the opening ends and the endgame begins, decided once when the game is loaded. */
  phases: ReviewPhases;
  /** Whose game it is and which way up the board is; optional so older loaders still type-check. */
  sides?: ReviewSides;
  /**
   * Seconds left on the mover's clock after each move, by ply - 1, from the
   * PGN's `[%clk]` comments (`clocksOf`); a move without one is undefined.
   * Absent, or empty, when the PGN has no clocks: the board shows none then.
   */
  clocks?: Array<number | undefined>;
  /** Seconds each player started with, from the time control (`initialClockOf`); what the clocks show at the start position. */
  initialClock?: number;
}

/** A line the reader played instead of the game, from the ply it branches at: v1's exploration. */
export interface ReviewExploration {
  /** The game ply the line starts from; `positions[0]` is the game's position there. */
  fromPly: number;
  moves: Move[];
  /** `positions[i]` is after `moves[i - 1]`; the last one is `fen`. */
  positions: Fen[];
  /** The position on the board. */
  fen: Fen;
}

export type ReviewStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'failed';

export interface ReviewState {
  status: ReviewStatus;
  gameId?: string;
  game?: ReviewGame;
  /** 0 is the starting position; `moves.length` is after the last move. While a line is open, the ply it branched from. */
  ply: number;
  /** The reader's own line, when they have left the game. */
  exploring?: ReviewExploration;
  /**
   * A position the reader is looking back at, as a ply along the path the
   * board has travelled (see `headPly`); absent when the board shows the head.
   */
  browse?: number;
  error?: string;
  // What the session derives once for every panel; absent until a game is open.
  /** The game's stages, from its phases. */
  stages?: Stages;
  /** The owner's colour; undefined when the owner played neither side or both. */
  reader?: Color;
  /** The game's stored scan, followed as it grows. */
  scan?: GameScan;
  /** Where a running scan is. */
  scanning?: { done: number; total: number };
  /** The opening book as this game reads it, once loaded. */
  book?: GameBook;
  bookStatus?: 'idle' | 'loading' | 'ready' | 'unavailable';
  /** The book's moves from the board's position, and whether they take the best-moves place. */
  theory?: ReviewTheory;
  /** The constraint scope of this board (a review tab); cards read constraints for it. */
  scope?: ConstraintScope;
}

export interface Review {
  state(): ReviewState;
  /** Called with every change; returns the unsubscribe. */
  subscribe(listener: (state: ReviewState) => void): () => void;
  /** Open a game by id, or nothing. */
  open(gameId: string | undefined): Promise<void>;
  /** Go to a ply of the game; drops any open line. */
  go(ply: number): void;
  /**
   * Stepping through the game. While a line is open, `stepBackward` takes
   * back the line's last move (the last one lands on the branch ply) and
   * `toStart` drops the line and goes to the start; `stepForward` and
   * `toEnd` do nothing.
   */
  stepBackward(): void;
  stepForward(): void;
  toStart(): void;
  toEnd(): void;
  /**
   * Play a move from the position on the board. The game's own next move
   * steps forward; any other legal move starts or extends a line. Returns
   * false when the move is illegal or no game is open.
   */
  play(move: MoveInput | San): boolean;
  /** Drop the line and land on the ply it branched from. */
  returnToGame(): void;
  /**
   * Look at an earlier position along the path, 0 up to the head, without
   * moving the head; `undefined`, or the head itself, shows the head again.
   * Nothing can be played while looking back.
   */
  browse(ply: number | undefined): void;
}

/** What `createReview` needs from the rules of chess to play a move. */
export interface ReviewRules {
  play(fen: Fen, move: MoveInput | San): { move: Move; fen: Fen } | undefined;
}

/** Keeps a ply inside the game. */
export function clampPly(ply: number, moveCount: number): number {
  if (!Number.isFinite(ply)) return 0;
  return Math.min(Math.max(Math.trunc(ply), 0), moveCount);
}

/** True while the reader has left the game for a line of their own. */
export function isExploring(state: ReviewState): boolean {
  return !!state.exploring;
}

/** The far end of the path the board has travelled: the line's end, or the game's ply. */
export function headPly(state: ReviewState): number {
  return state.exploring ? state.exploring.fromPly + state.exploring.moves.length : state.ply;
}

/** The ply along the path the board shows: the one looked back at, or the head. */
export function viewPly(state: ReviewState): number {
  return state.browse ?? headPly(state);
}

/** True while the reader is looking back at an earlier position. */
export function isBrowsing(state: ReviewState): boolean {
  return state.browse !== undefined;
}

/** The position at a ply along the path: the game's up to the line's start, then the line's. */
function pathPosition(state: ReviewState, ply: number): Fen | undefined {
  const line = state.exploring;
  if (line && ply > line.fromPly) return line.positions[ply - line.fromPly];
  return state.game?.positions[ply];
}

/** The move that led to a ply along the path, if any. */
function pathMove(state: ReviewState, ply: number): Move | undefined {
  if (ply <= 0) return undefined;
  const line = state.exploring;
  if (line && ply > line.fromPly) return line.moves[ply - line.fromPly - 1];
  return state.game?.moves[ply - 1];
}

/** The position on the board for a state, if a game is open: the one looked back at, the line's end, or the game's ply. */
export function positionAt(state: ReviewState): Fen | undefined {
  if (state.browse !== undefined) return pathPosition(state, state.browse);
  if (state.exploring) return state.exploring.fen;
  return state.game?.positions[state.ply];
}

/** The move that led to the current position, if any. */
export function lastMoveAt(state: ReviewState): Move | undefined {
  if (state.browse !== undefined) return pathMove(state, state.browse);
  if (state.exploring) {
    const n = state.exploring.moves.length;
    return n > 0 ? state.exploring.moves[n - 1] : state.game?.moves[state.exploring.fromPly - 1];
  }
  return state.ply > 0 ? state.game?.moves[state.ply - 1] : undefined;
}

/** "12. Nf3" or "12... Nf6" for the move at a ply, 1-based. */
export function moveLabel(game: ReviewGame, ply: number): string {
  const move = game.moves[ply - 1];
  if (!move) return '';
  const number = Math.ceil(ply / 2);
  return `${number}${ply % 2 === 1 ? '.' : '...'} ${move.san}`;
}

export type GameLoader = (id: string) => Promise<ReviewGame | undefined>;

/**
 * The reference implementation over a loader: a `ReviewSession`. A provider
 * supplies how a game is found and, to let the reader play, the rules; given
 * the analysis and openings APIs, the session also loads the game's scan and
 * book once and derives what the panels read from them.
 */
export function createReview(load: GameLoader, rules?: ReviewRules, services?: ReviewServices): ReviewSession {
  return new ReviewSession(load, rules, services);
}
